<template>
  <v-row dense :justify="showNewRateIcon ? 'end' : 'start'" align="center">
    <v-col cols="12" md="" v-if="displayFilter && !hideFilterControls">
      <v-row class="mb-0 ml-0">
        <v-card
          v-if="!groupBy"
          outlined
          :style="{ maxWidth: $vuetify.breakpoint.mdAndUp ? '350px' : '100%', borderRadius: '3vmax', display: 'flex', justifyContent: 'center', alignItems: 'center' }"
        >
          <v-select
            rounded
            solo
            flat
            dense
            clearable
            color="rgba(0,0,0,0.6)"
            :prepend-inner-icon="IconHelpers.getIconForObjectType('speciesGroup')"
            class="mb-n6 narrow"
            :label="$t('speciesGroup')"
            :items="speciesGroupSelectionOptions"
            v-model="filterBySpeciesGroup"
            style="min-width: 200px;"
          >
          </v-select>
        </v-card>

        <v-card
        outlined
        :style="{ maxWidth: $vuetify.breakpoint.mdAndUp ? '350px' : '100%', borderRadius: '3vmax', display: 'flex', justifyContent: 'center', alignItems: 'center' }"
        v-if="expansion !== null && expansion !== undefined && expansion?.length > 0">
        <v-select
          rounded
          solo
          flat
          dense
          clearable
          :disabled="expansion.length === 1"
          color="rgba(0,0,0,0.6)"
          :prepend-inner-icon="IconHelpers.getIconForObjectType('product')"
          class="mb-n6 narrow"
          :label="$t('product')"
          :items="productSelectionOptions"
          item-text="text"
          item-value="value"
          v-model="expansionProduct"
          style="min-width: 200px;"
        >
        </v-select>
      </v-card>
      </v-row>
    </v-col>
    <v-spacer v-else/>

    <v-col v-if="showGroupBySpeciesGroupIcon && !hideFilterControls" cols="auto">
      <Icon
      margin="mb-2 mt-0"
      @icon-clicked="groupBySpeciesGroupToggled"
      :icon="speciesGroupToggleIcon"
      :small="false"
      :tooltipText="groupBySpeciesGroupTooltip"
      ></Icon>
    </v-col>

    <v-col v-if="propShowExpansionIcon" cols="auto">
      <Icon
      margin="mb-2 mt-0"
      icon="mdi-alpha-e-circle-outline"
      :tooltipText="expansion ? $t('hideExpansion') : $t('showExpansion')"
      :small="false"
      @icon-clicked="expansionClicked"
      />
    </v-col>

    <v-col v-if="!editingRates && !startInEditMode && crud && lines.length > 0" cols="auto">
      <Icon
      icon="mdi-pencil"
      :tooltipText="$t('editRates')"
      @icon-clicked="enableEditMode"
      margin="mb-2 mt-0"
      :small="false"/>
    </v-col>

    <v-col v-if="editingRates && !startInEditMode" cols="auto">
      <Icon
      icon="mdi-pencil-off"
      :tooltipText="$t('cancelEdit')"
      @icon-clicked="cancelEditMode"
      margin="mb-2 mt-0"
      :small="false"
      iconColor="black"/>
    </v-col>

    <v-col v-if="showNewRateIcon" cols="auto">
      <Icon
      icon="mdi-plus"
      :disabled="newLineDisabled"
      :tooltipText="$t('newRate')"
      @icon-clicked="newClicked"
      margin="mb-2 mt-1"
      :small="false"/>
    </v-col>

    <v-col cols="12">
      <v-card :outlined="!flat" :flat="flat" :class="ratesCardClass">
        <RateSheetLineTable
          :lines="lineItemsFiltered"
          :accountId="accountId"
          :rateSheetInteractionMode="rateSheetInteractionMode"
          :loading="loading"
          :propGroupBy="groupBy"
          :itemClass="itemClass"
          :tableHeightCalculationData="tableHeightCalculationData"
          :expansion="expansionFiltered"
          :linesAreFiltered="!!filterBySpeciesGroup"
          :baseRateCostCalculationId="baseRateCostCalculationId"
          :originalLines="lines"
          :editingRates="editingRates"
          @edit="editClicked"
          @delete="deleteClicked"
          @lock="lockClicked"
          @remove="removeClicked"
          @expansion-info-clicked="expansionInfoClicked"
          @rate-changed="handleRateChanged"
        />
      </v-card>
    </v-col>
  </v-row>
</template>

<script>
import IconHelpers from '@/utils/IconHelpers'
import { cloneDeep, uniq } from 'lodash'
import { RateSheetInteractionMode } from '@/components/activity/rate-sheet/RateSheet'

export default {
  name: 'RateSheetLineTableFilterable',

  components: {
    RateSheetLineTable: () => import('@/components/activity/rate-sheet/RateSheetLineTable.vue'),
    Icon: () => import('@/components/helper/Icon.vue')
  },

  props: {
    rateSheetInteractionMode: {
      type: Number,
      required: true
    },

    lines: {
      type: Array,
      required: true
    },

    accountId: {
      type: Number,
      required: true
    },

    loading: {
      type: Boolean,
      default: false
    },

    itemClass: {
      type: Function,
      required: false,
      default: () => 'angel-table-row'
    },

    tableHeightCalculationData: {
      type: Object,
      required: false,
      default: () => ({ auto: true })
    },

    triggerFadeTranslateDown: {
      type: Boolean,
      default: false
    },

    expansion: {
      type: Array,
      required: false,
      default: () => []
    },

    propShowExpansionIcon: {
      type: Boolean,
      default: false
    },

    newLineDisabled: {
      type: Boolean,
      default: false
    },

    baseRateCostCalculationId: {
      type: Number,
      required: false,
      default: null
    },

    crud: {
      type: Boolean,
      default: false
    },

    startInEditMode: {
      type: Boolean,
      default: false
    },

    hideFilterControls: {
      type: Boolean,
      default: false
    },

    propFilterBySpeciesGroup: {
      type: String,
      default: null
    },

    propGroupBySpeciesGroup: {
      type: Boolean,
      default: null
    },

    flat: {
      type: Boolean,
      default: false
    }
  },

  data: () => ({
    IconHelpers,
    filterBySpeciesGroup: null,
    groupBySpeciesGroup: false,
    tableIsFading: false,
    expansionProduct: null,
    editingRates: false,
    editedLines: [],
    originalLinesCopy: []
  }),

  created () {
    if ((this.expansion !== null || this.expansion !== undefined) && this.expansion?.length > 0) {
      this.expansionProduct = this.expansion[0].productId
    }
    if (this.startInEditMode) {
      this.enableEditMode()
    }
  },

  computed: {
    displayFilter () {
      return this.speciesGroupSelectionOptions.length > 1
    },

    speciesGroupSelectionOptions () {
      return uniq(this.lines.map(line => line.speciesGroup?.label ?? this.$t('notSpecified')))
    },

    productSelectionOptions () {
      return uniq(this.expansion.map(item => ({
        text: item.product ?? this.$t('notSpecified'),
        value: item.productId
      })), item => item.value)
    },

    expansionFiltered () {
      if (this.expansion === null) {
        return null
      }
      this.$emit('expansion-filtered', this.expansionProduct)
      return this.expansion.find(item => item.productId === this.expansionProduct)
    },

    lineItemsFiltered () {
      const activeFilter = this.propFilterBySpeciesGroup !== null ? this.propFilterBySpeciesGroup : this.filterBySpeciesGroup
      if (!activeFilter) return this.lines
      return this.lines.filter(line => {
        if (activeFilter === this.$t('notSpecified')) return line.hasNoSpeciesGroup()
        return line.hasSpeciesGroupByLabel(activeFilter)
      })
    },

    groupBy () {
      const groupActive = this.propGroupBySpeciesGroup !== null ? this.propGroupBySpeciesGroup : this.groupBySpeciesGroup
      return groupActive && this.$vuetify.breakpoint.mdAndUp ? 'speciesGroup' : undefined
    },

    speciesGroupToggleIcon () {
      return IconHelpers.getIconForObjectType('speciesGroup', !this.groupBySpeciesGroup)
    },

    groupBySpeciesGroupTooltip () {
      return this.$t(this.groupBySpeciesGroup ? 'disableGroupingByCategory' : 'groupByCategory', { category: this.$t('speciesGroup') })
    },

    showGroupBySpeciesGroupIcon () {
      return this.$vuetify.breakpoint.mdAndUp &&
        this.speciesGroupSelectionOptions.length > 1 &&
        !this.filterBySpeciesGroup &&
        [RateSheetInteractionMode.RateSheet.View, RateSheetInteractionMode.RateSheetDefinition.View].includes(this.rateSheetInteractionMode)
    },

    showNewRateIcon () {
      const creatingRateSheet = this.rateSheetInteractionMode === RateSheetInteractionMode.RateSheet.Create
      const editingRateSheet = this.rateSheetInteractionMode === RateSheetInteractionMode.RateSheet.Edit
      return creatingRateSheet || editingRateSheet
    },

    ratesCardClass () {
      return `overflow-hidden can-fade-translate-down ${this.tableIsFading ? 'fading-translate-down' : 'not-fading-translate-down'}`
    }
  },

  watch: {
    triggerFadeTranslateDown (newVal) {
      if (newVal) {
        this.doFadeTranslateDownAnimation()
      }
    },

    expansion () {
      if (this.expansion !== null && this.expansion.length > 0) {
        this.expansionProduct = this.expansion[0].productId
      } else {
        this.expansionProduct = null
      }
    },

    editedLines: {
      handler (newVal) {
        this.$emit('rates-edited', newVal)
      },
      deep: true
    },

    lines (newVal) {
      if (this.startInEditMode && newVal.length > 0 && this.editedLines.length === 0) {
        this.enableEditMode()
      }
    }
  },

  methods: {
    groupBySpeciesGroupToggled () {
      this.groupBySpeciesGroup = !this.groupBySpeciesGroup
      this.$emit('group-by-species-group-toggled', this.groupBySpeciesGroup)
    },

    editClicked (line) {
      this.$emit('edit', line)
    },

    deleteClicked (line) {
      this.$emit('delete', line)
    },

    removeClicked (line) {
      this.$emit('remove', line)
    },

    lockClicked (line) {
      this.$emit('lock', line)
    },

    newClicked () {
      this.$emit('new')
    },

    doFadeTranslateDownAnimation () {
      this.tableIsFading = true
      setTimeout(() => {
        this.tableIsFading = false
      }, 150)
    },

    expansionClicked () {
      this.$emit('expansion-clicked')
    },

    expansionInfoClicked () {
      this.$emit('expansion-info-clicked')
    },

    enableEditMode () {
      this.editingRates = true
      this.editedLines = this.lines.map(line => ({ ...line }))
      this.originalLinesCopy = cloneDeep(this.lines)
    },

    cancelEditMode () {
      this.editingRates = false
      this.editedLines = []
      this.$emit('restore-ratesheet-lines', this.originalLinesCopy)
      this.originalLinesCopy = []
    },

    handleRateChanged (lineId, newRate) {
      const parsed = parseFloat(newRate)
      // Check if it is not NaN before setting because there is a brief moment in which clearing the text-field causes NaN
      if (!isNaN(parsed)) {
        const idx = this.editedLines.findIndex(
          l => (l.rateSheetDefinitionLineId ?? l.rateSheetLineId ?? l.uuid) === lineId
        )
        if (idx !== -1) {
          this.editedLines.splice(idx, 1, { ...this.editedLines[idx], rate: parsed })
        }
      }
    }
  }
}

</script>
